import React, { FC, Fragment } from 'react';
import { Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import { compact } from 'lodash';

import { GroupAvatar, TextTransform, UserAvatar } from 'components';
import {
  ContentType,
  PostSource,
  PublicUserProfileFragment,
  SearchContentInput,
  UserFeedFollowGroupFragment,
} from 'api';

interface Props {
  filter: SearchContentInput;
  setFilter: (filter: SearchContentInput) => void;
  isEmpty: boolean;
  selectedUsers: PublicUserProfileFragment[];
  selectedGroups: UserFeedFollowGroupFragment[];
}

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
}));

const FilterChips: FC<Props> = ({ filter, setFilter, isEmpty, selectedUsers, selectedGroups }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  function onDeleteList(list: 'group_list' | 'submitter_list', id: string) {
    const updatedList = filter[list].filter(item => item !== id);
    if (!updatedList.length) {
      delete filter[list];
      setFilter({ ...filter });
      return;
    }

    setFilter({
      ...filter,
      [list]: updatedList,
    });
  }

  function renderBrandedChips() {
    if (filter.is_branded === undefined) {
      return null;
    }

    if (filter.is_branded) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('common:branded')}
          onDelete={() => {
            delete filter.is_branded;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    return (
      <Chip
        clickable
        variant="outlined"
        label={t('posting:unbranded')}
        onDelete={() => {
          delete filter.is_branded;
          setFilter({ ...filter });
        }}
        deleteIcon={<CloseIcon fontSize="small" />}
      />
    );
  }

  function renderCommentaryChips() {
    if (filter.commentary === undefined) {
      return null;
    }

    if (filter.commentary) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('admin:commentary')}
          onDelete={() => {
            delete filter.commentary;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    return (
      <Chip
        clickable
        variant="outlined"
        label={t('admin:noCommentary')}
        onDelete={() => {
          delete filter.commentary;
          setFilter({ ...filter });
        }}
        deleteIcon={<CloseIcon fontSize="small" />}
      />
    );
  }

  function renderSubmitterRoleChips() {
    if (filter.submitter_role === undefined) {
      return null;
    }

    return (
      <Chip
        clickable
        variant="outlined"
        label={<TextTransform transform="capitalize">{filter.submitter_role}</TextTransform>}
        onDelete={() => {
          delete filter.submitter_role;
          setFilter({ ...filter });
        }}
        deleteIcon={<CloseIcon fontSize="small" />}
      />
    );
  }

  function renderCTAChips() {
    if (filter.is_shareable === undefined && filter.is_engageable === undefined) {
      return null;
    }

    if (filter.is_shareable && filter.is_engageable) {
      return (
        <Fragment>
          <Chip
            clickable
            variant="outlined"
            label={t('posting:shareable')}
            onDelete={() => {
              delete filter.is_shareable;
              setFilter({ ...filter });
            }}
            deleteIcon={<CloseIcon fontSize="small" />}
          />
          <Chip
            clickable
            variant="outlined"
            label={t('posting:engageable')}
            onDelete={() => {
              delete filter.is_engageable;
              setFilter({ ...filter });
            }}
            deleteIcon={<CloseIcon fontSize="small" />}
          />
        </Fragment>
      );
    }

    if (filter.is_shareable) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('posting:shareable')}
          onDelete={() => {
            delete filter.is_shareable;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    if (filter.is_engageable) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('posting:engageable')}
          onDelete={() => {
            delete filter.is_engageable;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    return null;
  }

  function renderGroupChips() {
    if (filter.group_list === undefined || filter.group_list.length === 0) {
      return null;
    }

    const groupList = compact(
      filter.group_list.map(id => selectedGroups.find(group => group.id === id))
    );
    return groupList.map(group => (
      <Chip
        clickable
        key={group.id}
        variant="outlined"
        label={group.title}
        onDelete={() => onDeleteList('group_list', group.id)}
        avatar={<GroupAvatar group={group} size={24} />}
        deleteIcon={<CloseIcon fontSize="small" />}
      />
    ));
  }

  function renderContentTypeChips() {
    if (filter.content_type === undefined) {
      return null;
    }

    if (filter.content_type === ContentType.Link) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('common:link')}
          onDelete={() => {
            delete filter.content_type;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    if (filter.content_type === ContentType.Text) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('common:Text')}
          onDelete={() => {
            delete filter.content_type;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    if (filter.content_type === ContentType.Photo) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('common:image')}
          onDelete={() => {
            delete filter.content_type;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    if (filter.content_type === ContentType.Video) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={t('common:video')}
          onDelete={() => {
            delete filter.content_type;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    return null;
  }

  function renderSourceChips() {
    if (filter.source === undefined) {
      return null;
    }

    if (filter.source === PostSource.External) {
      return (
        <Chip
          clickable
          variant="outlined"
          label={`${t('common:source')}: ${t('common:external')}`}
          onDelete={() => {
            delete filter.source;
            setFilter({ ...filter });
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    }

    return (
      <Chip
        clickable
        variant="outlined"
        label={`${t('common:source')}: ${t('common:user')}`}
        onDelete={() => {
          delete filter.source;
          setFilter({ ...filter });
        }}
        deleteIcon={<CloseIcon fontSize="small" />}
      />
    );
  }

  function renderSubmitterChips() {
    if (filter.submitter_list === undefined || filter.submitter_list.length === 0) {
      return null;
    }

    const submitterList = compact(
      filter.submitter_list.map(id => selectedUsers.find(user => user.id === id))
    );
    return submitterList.map(user => {
      return (
        <Chip
          clickable
          key={user.id}
          variant="outlined"
          // @ts-ignore
          label={user.full_name || user.title}
          onDelete={() => onDeleteList('submitter_list', user.id)}
          avatar={<UserAvatar user={user} size={24} />}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    });
  }

  if (isEmpty) {
    return null;
  }

  return (
    <div className={classes.container}>
      {renderGroupChips()}
      {renderSubmitterChips()}
      {renderContentTypeChips()}
      {renderSubmitterRoleChips()}
      {renderCTAChips()}
      {renderSourceChips()}
      {renderCommentaryChips()}
      {renderBrandedChips()}
    </div>
  );
};

export default FilterChips;
